/**
 * @file toolbar-toggle-button.tsx
 *
 * An MD3 Expressive toggle button with icon and text label for Toolbar slots.
 * Implements the 'Button toggleable' building block from MD3 Toolbar guidelines.
 *
 * @see https://m3.material.io/components/toolbars/guidelines (Anatomy → Flexibility & slots)
 */

import { Slot } from "@radix-ui/react-slot";
import type { HTMLMotionProps } from "motion/react";
import { domMax, LazyMotion, m } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { BUTTON_RADIUS_SPRING } from "../buttons/button/button-tokens";
import { Ripple, useRippleState } from "../ripple";
import { FAST_SPATIAL_SPRING } from "../shared/motion-tokens";
import { useToolbarContext } from "./toolbar-context";
import {
	ToolbarToggleButtonRadiusTokens,
	ToolbarToggleButtonTokens,
} from "./toolbar-tokens";

const MOTION_PROP_KEYS = [
	"animate",
	"exit",
	"initial",
	"transition",
	"variants",
	"whileHover",
	"whileTap",
	"whileFocus",
	"whileDrag",
	"whileInView",
	"onAnimationStart",
	"onAnimationComplete",
	"onUpdate",
	"onDragStart",
	"onDragEnd",
	"onDrag",
	"onDirectionLock",
	"onDragTransitionEnd",
	"layout",
	"layoutId",
	"onLayoutAnimationComplete",
] as const;

export interface ToolbarToggleButtonProps
	extends Omit<HTMLMotionProps<"button">, "children"> {
	/**
	 * Visual emphasis variant.
	 * @default "standard"
	 */
	emphasis?: "standard" | "tonal" | "filled";
	/**
	 * Whether the button is in a selected/active state.
	 * @default false
	 */
	selected?: boolean;
	/**
	 * Leading icon component or node.
	 */
	icon?: React.ReactNode;
	/**
	 * Button text label / content.
	 */
	children?: React.ReactNode;
	/**
	 * Enable symmetric spring expand on press.
	 * The button expands equally to the left and right — matching ButtonDistribute behavior.
	 * @default true
	 */
	pressExpand?: boolean;
	/**
	 * Fraction of the button width added symmetrically on press.
	 * e.g. 0.06 → scaleX goes from 1.0 to 1.06 (3% each side).
	 * @default 0.06
	 */
	pressExpandRatio?: number;
	/**
	 * Enable MD3 state layer ripple effect on click.
	 * @default true
	 */
	ripple?: boolean;
	/**
	 * Delegation prop to merge props onto a child element (Radix Slot).
	 */
	asChild?: boolean;
	className?: string;
}

/**
 * An MD3 pill-shaped toggle button with optional leading icon and text label.
 *
 * Meets MD3 Expressive touch-target requirements (48dp height minimum, ~95dp width).
 * Features Material Design 3 Expressive spring press animation:
 * - Symmetric horizontal expand (scaleX) with `FAST_SPATIAL_SPRING` (has bounce)
 * - Border-radius morphing via `BUTTON_RADIUS_SPRING` (fast.effects, no overshoot)
 * - Configurable ripple state layer
 *
 * @example
 * ```tsx
 * const [selected, setSelected] = useState(false);
 *
 * <ToolbarToggleButton
 *   selected={selected}
 *   onClick={() => setSelected(!selected)}
 *   icon={<EditIcon />}
 * >
 *   Edit
 * </ToolbarToggleButton>
 * ```
 */
export const ToolbarToggleButton = React.forwardRef<
	HTMLButtonElement,
	ToolbarToggleButtonProps
>(
	(
		{
			emphasis = "standard",
			selected = false,
			icon,
			children,
			pressExpand = true,
			pressExpandRatio = 0.06,
			ripple = true,
			asChild = false,
			className,
			type = "button",
			disabled,
			onPointerDown: onPointerDownProp,
			onPointerUp: onPointerUpProp,
			onPointerLeave: onPointerLeaveProp,
			...props
		},
		ref,
	) => {
		const context = useToolbarContext();

		const [isPressed, setIsPressed] = React.useState(false);

		const {
			ripples,
			onPointerDown: handleRipplePointerDown,
			removeRipple,
		} = useRippleState({
			disabled: Boolean(disabled) || !ripple,
		});

		const handlePointerDown = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(true);
				if (!disabled && ripple) {
					handleRipplePointerDown(e);
				}
				onPointerDownProp?.(e);
			},
			[disabled, ripple, handleRipplePointerDown, onPointerDownProp],
		);

		const handlePointerUp = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerUpProp?.(e);
			},
			[onPointerUpProp],
		);

		const handlePointerLeave = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerLeaveProp?.(e);
			},
			[onPointerLeaveProp],
		);

		const baseClasses =
			"relative overflow-hidden inline-flex items-center justify-center min-w-[95px] h-[48px] px-4 text-sm font-medium select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/40 disabled:pointer-events-none disabled:opacity-38";

		const variantClasses = {
			standard: selected
				? "bg-current/16 text-current hover:bg-current/20 active:bg-current/24"
				: "bg-transparent text-current hover:bg-current/8 active:bg-current/12",
			tonal: selected
				? "bg-m3-secondary-container text-m3-on-secondary-container hover:shadow-sm"
				: "bg-m3-surface-container-high text-m3-on-surface hover:bg-m3-surface-container-highest",
			filled: selected
				? "bg-m3-primary text-m3-on-primary hover:shadow-sm"
				: "bg-m3-surface-container-highest text-m3-on-surface hover:bg-m3-surface-container-high",
		};

		const combinedClassName = cn(
			baseClasses,
			variantClasses[emphasis],
			context?.itemClassName,
			className,
		);

		// Symmetric expand: scaleX grows from center (both sides equally)
		const animateConfig = React.useMemo(
			() => ({
				scaleX: pressExpand && isPressed ? 1 + pressExpandRatio : 1,
				borderRadius: isPressed
					? ToolbarToggleButtonRadiusTokens.pressedRadius
					: ToolbarToggleButtonRadiusTokens.pillRadius,
			}),
			[pressExpand, pressExpandRatio, isPressed],
		);

		const transitionConfig = React.useMemo(
			() => ({
				scaleX: FAST_SPATIAL_SPRING,
				borderRadius: BUTTON_RADIUS_SPRING,
			}),
			[],
		);

		const mainContent = (
			<span className="inline-flex items-center justify-center h-full gap-2">
				{icon && (
					<span
						className="shrink-0 inline-flex items-center justify-center"
						style={{
							width: ToolbarToggleButtonTokens.IconSize,
							height: ToolbarToggleButtonTokens.IconSize,
						}}
					>
						{icon}
					</span>
				)}
				{children &&
					(typeof children === "string" || typeof children === "number" ? (
						<span>{children}</span>
					) : (
						children
					))}
			</span>
		);

		if (asChild) {
			const child = React.Children.only(children) as React.ReactElement<{
				children?: React.ReactNode;
			}>;

			const childContent = child.props.children;

			const innerChildContent = (
				<>
					{ripple && (
						<Ripple
							ripples={ripples}
							onRippleDone={removeRipple}
							disabled={disabled}
						/>
					)}
					<span className="inline-flex items-center justify-center h-full gap-2">
						{icon ? (
							<>
								<span
									className="shrink-0 inline-flex items-center justify-center"
									style={{
										width: ToolbarToggleButtonTokens.IconSize,
										height: ToolbarToggleButtonTokens.IconSize,
									}}
								>
									{icon}
								</span>
								{typeof childContent === "string" ||
								typeof childContent === "number" ? (
									<span>{childContent}</span>
								) : (
									childContent
								)}
							</>
						) : (
							childContent
						)}
					</span>
				</>
			);

			const strippedProps = { ...props } as Record<string, unknown>;
			for (const key of MOTION_PROP_KEYS) delete strippedProps[key];
			delete strippedProps.children;

			return (
				<LazyMotion features={domMax} strict>
					<Slot
						ref={ref}
						aria-pressed={selected}
						className={combinedClassName}
						onPointerDown={
							handlePointerDown as unknown as React.PointerEventHandler<HTMLElement>
						}
						onPointerUp={
							handlePointerUp as unknown as React.PointerEventHandler<HTMLElement>
						}
						onPointerLeave={
							handlePointerLeave as unknown as React.PointerEventHandler<HTMLElement>
						}
						{...(strippedProps as React.HTMLAttributes<HTMLElement>)}
					>
						{React.cloneElement(child, { children: innerChildContent })}
					</Slot>
				</LazyMotion>
			);
		}

		return (
			<LazyMotion features={domMax} strict>
				<m.button
					ref={ref}
					type={type}
					aria-pressed={selected}
					disabled={disabled}
					animate={animateConfig}
					transition={transitionConfig}
					initial={false}
					style={{ transformOrigin: "center" }}
					onPointerDown={handlePointerDown}
					onPointerUp={handlePointerUp}
					onPointerLeave={handlePointerLeave}
					className={combinedClassName}
					{...props}
				>
					{ripple && (
						<Ripple
							ripples={ripples}
							onRippleDone={removeRipple}
							disabled={disabled}
						/>
					)}
					{mainContent}
				</m.button>
			</LazyMotion>
		);
	},
);

ToolbarToggleButton.displayName = "ToolbarToggleButton";
